import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Section, DeleteBtn } from "./common";
import { TabsContent } from "@/components/ui/tabs";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function RegistrationsTab() {
  const [items, setItems] = useState([]);
  const [verifying, setVerifying] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/registrations");
      setItems(data);
    } catch {
      toast.error("Failed to load registrations");
    }
  };

  useEffect(() => { load(); }, []);

  const imgSrc = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);

  const verify = async (r) => {
    setVerifying(r.id);
    try {
      await api.post(`/registrations/${r.id}/verify`);
      toast.success(`${r.player_name} marked as paid — confirmation email sent`);
      load();
    } catch {
      toast.error("Couldn't verify — try again");
    } finally {
      setVerifying(null);
    }
  };

  return (
    <TabsContent value="players" data-testid="admin-players-tab">
      <Section title={`Auction pool registrations (${items.length})`}>
        <ul className="divide-y divide-black/5" data-testid="admin-registrations-list">
          {items.map((r) => (
            <li key={r.id} className="py-4 flex items-start justify-between gap-4" data-testid={`admin-registration-${r.id}`}>
              <div className="flex items-start gap-4 min-w-0">
                {r.image_url ? (
                  <img src={imgSrc(r.image_url)} alt={r.player_name} className="w-14 h-14 rounded-full object-cover border border-black/10 shrink-0" />
                ) : (
                  <span className="w-14 h-14 rounded-full bg-league-blue/10 text-league-blue flex items-center justify-center font-oswald font-semibold text-lg shrink-0">
                    {r.player_name.charAt(0)}
                  </span>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-league-ink flex flex-wrap items-center gap-2">
                    {r.player_name}
                    {r.payment_status === "verified" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-600 text-[10px] font-bold uppercase tracking-wider" data-testid={`admin-registration-paid-${r.id}`}>
                        <BadgeCheck size={11} /> Paid
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-400/50 text-amber-600 text-[10px] font-bold uppercase tracking-wider" data-testid={`admin-registration-pending-${r.id}`}>
                        Payment pending
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{r.whatsapp}{r.email ? ` • ${r.email}` : ""}</p>
                  <p className="text-xs text-league-ink/70 mt-1">
                    {r.batting_style}{r.bowling_style && r.bowling_style !== "None" ? ` • ${r.bowling_style}` : ""}
                    {(r.jersey_name || r.jersey_number || r.jersey_size) && ` • Jersey ${r.jersey_size} "${r.jersey_name}" #${r.jersey_number}`}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 mt-1.5">
                    {r.cricheroes_link && (
                      <a href={r.cricheroes_link} target="_blank" rel="noreferrer" className="text-xs text-league-blue font-semibold hover:underline" data-testid={`admin-registration-ch-${r.id}`}>
                        CricHeroes profile ↗
                      </a>
                    )}
                    {r.payment_proof_url && (
                      <a href={imgSrc(r.payment_proof_url)} target="_blank" rel="noreferrer" className="text-xs text-league-blue font-semibold hover:underline" data-testid={`admin-registration-proof-${r.id}`}>
                        Payment screenshot ↗
                      </a>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.payment_status !== "verified" && r.payment_proof_url && (
                  <button
                    onClick={() => verify(r)}
                    disabled={verifying === r.id}
                    data-testid={`admin-registration-verify-${r.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-[11px] font-bold uppercase tracking-wider transition-colors hover:bg-emerald-700 disabled:opacity-60 cursor-pointer"
                  >
                    {verifying === r.id ? <Loader2 size={12} className="animate-spin" /> : <BadgeCheck size={12} />}
                    Verify payment
                  </button>
                )}
                <DeleteBtn testid={`admin-registration-delete-${r.id}`} onClick={async () => { await api.delete(`/registrations/${r.id}`).catch(() => toast.error("Delete failed")); load(); }} />
              </div>
            </li>
          ))}
          {items.length === 0 && <li className="py-4 text-sm text-muted-foreground">No player registrations yet — they arrive from the Register page.</li>}
        </ul>
      </Section>
    </TabsContent>
  );
}
